import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Lock, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState("form"); // form | sending | done
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) return setError("Your new password must be at least 6 characters.");
    if (password !== confirm) return setError("The two passwords do not match.");
    setState("sending");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setState("done");
    } catch (err) {
      setError(apiError(err?.response?.data?.detail));
      setState("form");
    }
  };

  const input =
    "w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring";

  return (
    <div className="mx-auto max-w-xl px-4 py-16 sm:px-6">
      <Seo title="Choose a new password" noindex />
      <div className="rounded-2xl border border-[#C89B3C]/30 bg-[#161118]/80 p-8">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[#C89B3C]/15 text-[#C89B3C]">
          <Lock className="h-6 w-6" />
        </div>

        {state === "done" ? (
          <>
            <h1 className="mt-4 text-center font-serif text-2xl font-semibold text-amber-50">Password changed</h1>
            <p className="mt-3 text-center text-amber-50/80">You can now sign in with your new password.</p>
            <div className="mt-6 flex justify-center">
              <Link
                to="/login"
                className="rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black transition-transform hover:scale-105"
              >
                Sign in
              </Link>
            </div>
          </>
        ) : !token ? (
          <>
            <h1 className="mt-4 text-center font-serif text-2xl font-semibold text-amber-50">This link did not work</h1>
            <p className="mt-3 text-center text-amber-50/80">
              Please ask for a new one.{" "}
              <Link to="/forgot-password" className="text-[#C89B3C] hover:underline">Reset my password</Link>
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-4 text-center font-serif text-2xl font-semibold text-amber-50">Choose a new password</h1>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">New password</label>
                <input required type="password" autoComplete="new-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className={input} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Confirm new password</label>
                <input required type="password" autoComplete="new-password" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} className={input} />
              </div>
              {error && (
                <p className="text-sm text-destructive">
                  {error}{" "}
                  {error.includes("expired") && (
                    <Link to="/forgot-password" className="underline">Ask for a new link</Link>
                  )}
                </p>
              )}
              <button
                type="submit"
                disabled={state === "sending"}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#C89B3C] px-6 py-3 font-semibold text-black transition-transform hover:scale-[1.02] disabled:opacity-60"
              >
                {state === "sending" && <Loader2 className="h-4 w-4 animate-spin" />} Change password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}